import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import TacticalBoardPanel from "@/components/TacticalBoardPanel";
import AttachmentsContainer from "@/components/AttachmentsContainer";
import Breadcrumbs from "@/components/Breadcrumbs";
import EmptyData from "@/components/EmptyData";
import MainActions, {usePageWritable} from "@/components/MainActions";
import {
	Cell,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
} from "recharts";
import Rating from "@/components/Rating";
import Tabs from "@/components/Tabs";
import ViewField from "@/components/Input/ViewField";
import VLFCard from "@/components/Card";
import VLFModal from "@/components/Modal";
import {useInjuryConfirmation} from "@/utils/hooks";
import HandleCopyExercisesModal from "@/pages/Manager/Match/handleCopyExercises";
import HandleExerciseModal from "@/pages/Manager/Match/handleExercise";
import HandleRPEModal from "@/pages/Manager/Match/handleRPE";
import RpeTab from "@/pages/Manager/Match/RpeTab";
import AttendanceTab from "@/pages/Manager/Training/AttendanceTab";
import ExercisesTab from "@/pages/Manager/Training/ExercisesTab";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import {AttachmentAPI} from "@/api/attachment";
import {ExerciseAPI} from "@/api/exercise";
import {MatchAPI} from "@/api/match";
import {MedicalAPI, isInjuryActiveAt} from "@/api/medical";
import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";
import {TrainingAPI} from "@/api/training";

import {setTrainingTab} from "@/actions";

import {
	ERROR_OPERATION,
	getInternalClubId,
	SUCCESSFUL_OPERATION,
	attendanceStatusOptions,
	rpeOptions,
} from "@/utils/constants";
import {getAttachment, isInternalPlayer} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";
import {generateTrainingPDF} from "@/utils/generateTrainingPDF";
import {SERIES} from "@/style/utils";

import "./index.scss";

function TrainingPage(props) {
	const {handleNotification} = props;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const currentTab = useSelector((state) => state.trainingTab);
	const playerId = useSelector((state) => state?.user?.playerId);
	const userRole = useSelector((state) => state?.user?.roleName);
	const writable = usePageWritable();
	const backPath = userRole === "Player" ? "/trainings" : "/calendar";

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
	});

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [trainingData, setTrainingData] = useState({});
	const [teamPlayers, setTeamPlayers] = useState([]);
	const [otherPlayers, setOtherPlayers] = useState([]);
	const [attachments, setAttachments] = useState([]);
	const [selectedBoardId, setSelectedBoardId] = useState(null);
	useEffect(() => {
		setSelectedBoardId(null);
	}, [id]);
	const editTacticalBoard = (boardId) => {
		setSelectedBoardId(boardId);
		dispatch(setTrainingTab("tactical_board"));
	};

	const [formData, setFormData] = useState({});

	const [openCopyExercisesModal, setOpenCopyExercisesModal] = useState(false);
	const [openRPEModal, setOpenRPEModal] = useState(false);
	const [openExerciseModal, setOpenExerciseModal] = useState(false);

	const [initialData, setInitialData] = useState();
	const [entityToDelete, setEntityToDelete] = useState({});

	const [attendance, setAttendance] = useState("");

	const getTraining = async (silent = false) => {
		if (!silent) setLoading(true);
		try {
			const response = await TrainingAPI.getById(id);
			setTrainingData(response);

			if (userRole === "Player") {
				const playerMeasurement =
					response.trainingRPE?.filter((ps) => ps.playerId === playerId)?.[0] ||
					{};
				setFormData({
					...Object.assign({}, playerMeasurement),
					rpe: parseInt(playerMeasurement.rpe),
				});

				const playerConvocation = response.trainingConvocations?.filter(
					(c) => c.playerId === playerId,
				)?.[0];

				setAttendance(playerConvocation ? playerConvocation.status : undefined);
				setLoading(false);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getTeamPlayers = async () => {
		setLoading(true);
		try {
			const rosterId = trainingData?.rosterId;
			if (rosterId) {
				const [rosterRes, allRes] = await Promise.all([
					RosterAPI.getPlayers(rosterId),
					PlayerAPI.list({prospect: false, seasonId}),
				]);
				const rosterPlayers = (rosterRes || [])
					.map((rp) => rp.player)
					.filter((p) => p && isInternalPlayer(p));
				const rosterIds = new Set(rosterPlayers.map((p) => p.id));
				setTeamPlayers(rosterPlayers);
				setOtherPlayers(
					(allRes || []).filter(
						(p) => isInternalPlayer(p) && !rosterIds.has(p.id),
					),
				);
			} else {
				const response = await PlayerAPI.list({prospect: false, seasonId});
				setTeamPlayers((response || []).filter(isInternalPlayer));
				setOtherPlayers([]);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getTrainingAttachment = async () => {
		try {
			const response = await AttachmentAPI.list("training", id);
			setAttachments(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const [matchProximity, setMatchProximity] = useState({
		daysSince: null,
		daysUntil: null,
	});

	const [internalTeams, setInternalTeams] = useState([]);
	const getTeams = async () => {
		try {
			const response = await RosterAPI.list({
				seasonId,
				prospect: false,
				clubId: getInternalClubId(),
			});
			setInternalTeams(
				(response || []).filter(
					(r) => !r.clubId || r.clubId === getInternalClubId(),
				),
			);
		} catch (error) {
			// silently ignore
		}
	};
	useEffect(() => {
		if (seasonId) {
			getTeams();
		}
	}, [seasonId]);

	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const {injuryWarning, resolveInjuryWarning, confirmHealthyAttendance} =
		useInjuryConfirmation({
			date: trainingData?.startDate,
			handleNotification,
		});

	// Presenze a un tocco, senza caricamento a tutta pagina
	const handleAttendanceStatus = async (playerId, status, attendanceId) => {
		try {
			if (!(await confirmHealthyAttendance({playerId, status}))) return;
			if (!status) {
				await TrainingAPI.deleteAttendance(trainingData?.id, attendanceId);
			} else if (attendanceId) {
				await TrainingAPI.updateAttendance(trainingData?.id, attendanceId, {
					id: attendanceId,
					status,
				});
			} else {
				await TrainingAPI.createAttendance({
					trainingId: trainingData?.id,
					playerId,
					status,
				});
			}
			await getTraining(true);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const markAllPresent = async (playerIds) => {
		setLoading(true);
		try {
			await TrainingAPI.createMultipleAttendances(
				playerIds.map((playerId) => ({
					trainingId: trainingData?.id,
					playerId,
					status: "Presente",
				})),
			);
			await getTraining(true);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// Infortuni attivi alla data dell'allenamento, dall'area medica
	const [injuredIds, setInjuredIds] = useState(null);
	useEffect(() => {
		if (
			currentTab !== "training_attendance_players" ||
			!trainingData?.startDate ||
			!seasonId
		)
			return;
		let active = true;
		MedicalAPI.recordsList({seasonId})
			.then((records) => {
				if (!active) return;
				setInjuredIds(
					new Set(
						(records || [])
							.filter((r) =>
								(r.injuriesIllness || []).some((inj) =>
									isInjuryActiveAt(inj, trainingData.startDate),
								),
							)
							.map((r) => r.playerId),
					),
				);
			})
			// Senza accesso all'area medica le presenze restano manuali
			.catch(() => {});
		return () => {
			active = false;
		};
	}, [currentTab, trainingData?.startDate, seasonId]);

	const handlePlayerAttendance = async (data, attendanceId) => {
		setLoading(true);
		try {
			if (!(await confirmHealthyAttendance(data))) return;
			attendanceId
				? await TrainingAPI.updateAttendance(id, attendanceId, data)
				: await TrainingAPI.createAttendance(data);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleMeasurement = async (data, type, id) => {
		setLoading(true);
		try {
			if (data.rpe) {
				const rpe =
					userRole === "Player"
						? data.rpe || undefined
						: data.rpe.value || undefined;

				const playerMeasurement = playerId
					? trainingData.trainingRPE?.filter(
							(ps) => ps.playerId === playerId,
						)?.[0]
					: {};

				const body = {
					id: userRole === "Player" ? playerMeasurement?.id : id,
					trainingId: trainingData?.id,
					playerId: userRole === "Player" ? playerId : data.player.value,
					...data,
					rpe,
				};
				delete body.player;

				playerMeasurement?.id || id
					? await TrainingAPI.updateMeasurement(
							trainingData?.id,
							playerMeasurement?.id || id,
							body,
						)
					: await TrainingAPI.createMeasurement(body);
			}

			if (type === "multiple") {
				const body = [];
				Object.keys(data?.players)?.forEach((pk) => {
					if (data.players[pk]?.rpe?.value) {
						body.push({
							trainingId: trainingData?.id,
							playerId: data.players[pk]?.player?.value,
							rpe: data.players[pk]?.rpe?.value,
						});
					}
				});

				await TrainingAPI.createMeasurements(body);
			}

			if (userRole === "Player") {
				const playerConvocation = trainingData?.trainingConvocations?.filter(
					(c) => c.playerId === playerId,
				)?.[0];

				await handlePlayerAttendance(
					{
						id: playerConvocation?.id,
						trainingId: trainingData?.id,
						playerId,
						status: data.rpe ? attendanceStatusOptions[0].label : attendance,
					},
					playerConvocation?.id,
				);
				navigate("/trainings");
			} else {
				getTraining();
				handleClose();
			}
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteTraining = async () => {
		setLoading(true);
		try {
			await TrainingAPI.delete(trainingData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate(backPath);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const deleteAttachment = async () => {
		setLoading(true);
		if (entityToDelete?.id) {
			try {
				await AttachmentAPI.delete(entityToDelete?.id);
				if (selectedBoardId === entityToDelete?.id) setSelectedBoardId(null);
				handleNotification(SUCCESSFUL_OPERATION, "success");
				setOpenHandleConfirm(false);
				getTrainingAttachment();
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		} else {
			return;
		}
	};

	const deleteMeasurement = async () => {
		setLoading(true);
		try {
			await TrainingAPI.deleteMeasurement(trainingData?.id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getTraining();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setOpenRPEModal(false);
		setOpenExerciseModal(false);
		setInitialData();
	};

	// Builds initialData for all training modals, resolving the correct roster
	// so modals can look up players by roster ID rather than by category.
	const buildInitialData = (extra = {}) => {
		const matchedRoster = trainingData?.rosterId
			? internalTeams?.find((t) => t.id === trainingData.rosterId)
			: internalTeams?.find(
					(t) => t.playerCategoryId === trainingData?.playersCategoryId,
				);

		return {
			matchRosterId: trainingData?.rosterId ?? matchedRoster?.id,
			matchCategoryId:
				matchedRoster?.playerCategoryId ?? trainingData?.playersCategoryId,
			matchCategoryLabel: matchedRoster?.name ?? trainingData?.playersCategory,
			...extra,
		};
	};

	useEffect(() => {
		getTraining();
		getTrainingAttachment();
	}, [id]);

	useEffect(() => {
		if (trainingData?.id) {
			getTeamPlayers();
		}
	}, [trainingData?.id, trainingData?.rosterId]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!trainingData?.startDate) return;
		const proxyFrom = moment(trainingData.startDate)
			.subtract(30, "days")
			.format("YYYY-MM-DD");
		const proxyTo = moment(trainingData.startDate)
			.add(30, "days")
			.format("YYYY-MM-DD");
		MatchAPI.list({
			clubId: getInternalClubId(),
			fromDate: proxyFrom,
			toDate: proxyTo,
			seasonId,
		})
			.then((matches) => {
				const trainingDate = moment(trainingData.startDate);
				const sameRoster = (matches || []).filter((m) => {
					if (trainingData.rosterId)
						return (
							m.homeRosterId === trainingData.rosterId ||
							m.awayRosterId === trainingData.rosterId
						);
					return m.playersCategory === trainingData.playersCategory;
				});
				const sorted = sameRoster
					.filter((m) => m.startDate)
					.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
				const prev = [...sorted]
					.reverse()
					.find((m) => moment(m.startDate).isBefore(trainingDate));
				const next = sorted.find((m) =>
					moment(m.startDate).isAfter(trainingDate),
				);
				setMatchProximity({
					daysSince: prev
						? trainingDate
								.clone()
								.startOf("day")
								.diff(moment(prev.startDate).startOf("day"), "days")
						: null,
					daysUntil: next
						? moment(next.startDate)
								.startOf("day")
								.diff(trainingDate.clone().startOf("day"), "days")
						: null,
				});
			})
			.catch(() => {});
	}, [trainingData?.startDate, trainingData?.rosterId, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const [trainingExercises, setTrainingExercises] = useState([]);
	const [trainingChartData, setTrainingChartData] = useState([]);
	const getTrainingExercises = async (resetExercises = false) => {
		if (resetExercises) setLoading(true);
		try {
			if (resetExercises) {
				const promises = [];
				trainingExercises
					.filter((e) => e.id !== entityToDelete?.id)
					?.forEach((e, s) => {
						promises.push(
							TrainingAPI.updateDetail(trainingData?.id, e.id, {
								...e,
								sorting: s + 1,
							}),
						);
					});

				await Promise.all(promises);
			}

			const response = await TrainingAPI.getDetails(id);
			setTrainingExercises(response);

			setTrainingChartData(
				Object.values(
					response.reduce((acc, item) => {
						const type = item.exercise?.exerciseType || "Sconosciuto";
						const duration = item.duration || 0;

						if (!acc[type]) {
							acc[type] = {name: type, value: 0};
						}

						acc[type].value += duration;
						return acc;
					}, {}),
				),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleExercise = async (data, exerciseId) => {
		setLoading(true);
		try {
			let body = {trainingId: id};
			if (data.currentExercise) {
				body = {
					...body,
					...data,
					exerciseId: data?.currentExercise?.value,
				};
				delete body.currentExercise;
				delete body.exercise;
			} else {
				const response = await ExerciseAPI.create({
					...data,
					description: data.description || "",
					exerciseType: data.exerciseType || "",
					basicActivities: data.basicActivities,
					goalkeepers: data.goalkeepers,
				});
				body = {...body, ...data, exerciseId: response.id};
			}
			body.phase = data.currentPhase.value;
			exerciseId
				? await TrainingAPI.updateDetail(trainingData?.id, exerciseId, body)
				: await TrainingAPI.createDetail({
						...body,
						sorting:
							trainingExercises?.filter(
								(te) => parseInt(te.phase) === data.currentPhase.value,
							)?.length + 1,
					});
			getTrainingExercises();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteExercise = async () => {
		setLoading(true);
		try {
			const deleted = trainingExercises.find(
				(e) => e.id === entityToDelete?.id,
			);
			await TrainingAPI.deleteDetail(trainingData?.id, entityToDelete?.id);
			const promises = [];
			trainingExercises
				?.filter(
					(e) => e.id !== entityToDelete?.id && e.phase === deleted?.phase,
				)
				?.sort((a, b) => a.sorting - b.sorting)
				?.forEach((e, s) => {
					promises.push(
						TrainingAPI.updateDetail(trainingData?.id, e.id, {
							...e,
							sorting: s + 1,
						}),
					);
				});
			await Promise.all(promises);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getTrainingExercises();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleExerciseOrder = async (detail, direction) => {
		const newSorting =
			direction === "up" ? detail.sorting - 1 : detail.sorting + 1;
		const exerciseToMove = trainingExercises.find(
			(e) => e.sorting === newSorting && e.phase === detail.phase,
		);
		if (!exerciseToMove) return;
		setLoading(true);
		try {
			await TrainingAPI.updateDetail(trainingData?.id, detail.id, {
				...detail,
				sorting: newSorting,
			});
			await TrainingAPI.updateDetail(trainingData?.id, exerciseToMove.id, {
				...exerciseToMove,
				sorting: detail.sorting,
			});
			getTrainingExercises();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleCopyExercises = async (data) => {
		const promises = [];
		trainingExercises.forEach((te) => {
			const body = {...te, trainingId: data.training?.value};
			promises.push(TrainingAPI.createDetail(body));
		});

		setLoading(true);
		try {
			await Promise.all(promises);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setLoading(false);
			setOpenCopyExercisesModal(false);
			navigate("/trainings/" + data.training?.value);
		} catch (error) {
			// silently ignore
			setLoading(false);
			setOpenCopyExercisesModal(false);
		}
	};

	useEffect(() => {
		if (currentTab === "training_attendance_players") {
			getTeamPlayers(false);
		}

		if (currentTab === "rpe") {
			getTeamPlayers(false);
		}

		if (currentTab === "main_info" || currentTab === "training_exercises") {
			getTrainingExercises();
		}
	}, [currentTab]);

	const mainConfiguration = [
		{
			value:
				trainingData?.startDate &&
				moment(trainingData?.startDate).format("DD/MM/YYYY"),
			label: t("date_label"),
			size: "col-6",
		},
		{
			value: trainingData?.startDate
				? moment(trainingData?.startDate).format("HH:mm") +
					" - " +
					(trainingData?.endDate
						? moment(trainingData?.endDate).format("HH:mm")
						: "---")
				: "---",
			label: t("time_label"),
			size: "col-6",
		},
		{
			value: trainingData?.rosterName || trainingData?.playersCategory,
			label: "Rosa",
			size: "col-6",
		},
		{
			value: trainingData?.fieldName,
			label: "Campo",
			size: "col-6",
		},
		{
			value: trainingData.trainingConvocations?.filter(
				(c) => c.status === "Presente",
			).length,
			label: t("appearances_number"),
			size: "col-6",
		},
		{
			value: trainingData?.trainingDetails?.length,
			label: t("exercises_number"),
			size: "col-6",
		},
		{
			value: trainingData?.trainingRPE?.length,
			label: "N° RPE",
			size: "col-6",
		},
		{
			value:
				matchProximity.daysSince !== null
					? `+${matchProximity.daysSince}`
					: "---",
			label: "Giorni dall'ultima partita",
			size: "col-6",
		},
		{
			value:
				matchProximity.daysUntil !== null
					? `-${matchProximity.daysUntil}`
					: "---",
			label: "Giorni alla prossima partita",
			size: "col-6",
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(backPath),
			color: "info",
		},
	];

	if (userRole !== "Player" && trainingData?.id) {
		mainActions.push(
			{
				icon: <EditIcon />,
				title: "Modifica",
				onClick: () => navigate(`/trainings/${id}/edit`),
			},
			{
				icon: <PictureAsPdfIcon />,
				title: "PDF",
				variant: "outlined",
				onClick: () => {
					try {
						generateTrainingPDF(
							trainingData,
							teamPlayers,
							trainingExercises,
							t,
						);
					} catch {
						handleNotification("PDF non disponibile, riprova", "error");
					}
				},
			},
			{
				icon: <DeleteIcon />,
				title: "Elimina",
				variant: "outlined",
				color: "error",
				onClick: () => {
					setEntityToDelete({type: "training", id});
					setOpenHandleConfirm(true);
				},
			},
		);
	}

	// Azioni del tab aperto: dentro il tab, con il testo visibile
	const tabActions = [];

	if (currentTab === "rpe" && userRole !== "Player") {
		tabActions.push({
			icon: <AddIcon />,
			label: "Aggiungi RPE",
			onClick: () => {
				setInitialData(buildInitialData());
				setOpenRPEModal(true);
			},
		});
	}

	if (currentTab === "training_exercises" && userRole !== "Player") {
		tabActions.push({
			variant: "outlined",
			icon: <ContentCopyIcon />,
			label: "Copia esercizi",
			onClick: () => {
				setOpenCopyExercisesModal(true);
			},
			disabled: !trainingExercises?.length,
		});
	}

	if (currentTab === "rpe" && userRole === "Player") {
		mainActions.push({
			icon: <SaveIcon />,
			title: "Salva",
			onClick: () => handleMeasurement(formData, formData?.id),
			disabled: !formData.rpe && !attendance,
		});
	}

	const breadcrumbsElements = [
		userRole === "Player"
			? {label: "Allenamenti", path: "/trainings"}
			: {label: "Calendario", path: "/calendar"},
		{label: "/"},
	];

	// RPE solo per le categorie agonistiche (la categoria arriva dalla rosa)
	const trainingCategoryId =
		trainingData?.playersCategoryId ||
		internalTeams?.find((r) => r.id === trainingData?.rosterId)
			?.playerCategoryId;
	const trainingCategory = categories?.find((c) => c.id === trainingCategoryId);
	const hasRPE = !trainingCategory?.basicActivities;

	// Tutti gli interni a disposizione (rosa + altri), come nel match
	const allInternalPlayers = [...(teamPlayers || []), ...(otherPlayers || [])];

	const tabs = [
		{
			key: "main_info",
			label: t("informations"),
			render: () => {
				return (
					<div className="row no-margin">
						<div className="col-12 padding-small">
							<VLFCard
								title="Informazioni principali"
								content={
									<div className="row">
										<div className="col-12 col-lg-6">
											<ViewField
												configuration={mainConfiguration.filter(
													(c) => hasRPE || c.label !== "N° RPE",
												)}
											/>
										</div>
										<div className="col-12 col-lg-6">
											<div className="view-field-container mb-0">
												<div className="label">Tipologie esercizi</div>
											</div>
											<div className="d-flex align-items-center justify-content-center">
												{trainingExercises?.length ? (
													<ResponsiveContainer width="50%" aspect={1}>
														<PieChart
															margin={{top: 0, right: 0, bottom: 0, left: 0}}
														>
															<Pie
																data={trainingChartData}
																dataKey="value"
																nameKey="name"
																cx="50%"
																cy="50%"
																outerRadius="80%"
															>
																{trainingChartData.map((entry, index) => (
																	<Cell
																		key={`cell-${index}`}
																		fill={SERIES[index % SERIES.length]}
																	/>
																))}
															</Pie>
															<Tooltip />
															<Legend />
														</PieChart>
													</ResponsiveContainer>
												) : (
													<EmptyData />
												)}
											</div>
										</div>
									</div>
								}
								openable={false}
							/>
						</div>
						{userRole !== "Player" ? (
							<div className="col-sm-12 padding-small mt-2">
								<VLFCard
									title="Allegati"
									content={
										<AttachmentsContainer
											entityId={id}
											entityType="training"
											attachments={attachments}
											handleEdit={
												userRole !== "Analyst" ? editTacticalBoard : undefined
											}
											handleAdd={() => getTrainingAttachment()}
											handleDownload={handleDownload}
											handleDelete={(id) => {
												setEntityToDelete({type: "attachment", id});
												setOpenHandleConfirm(true);
											}}
											handleNotification={handleNotification}
										/>
									}
								/>
							</div>
						) : null}
					</div>
				);
			},
		},
		{
			key: "training_attendance_players",
			label: t("appearances"),
			hidden: userRole === "Player",
			render: () => (
				<div className="row no-margin">
					<AttendanceTab
						trainingData={trainingData}
						teamPlayers={teamPlayers}
						otherPlayers={otherPlayers}
						injuredIds={injuredIds}
						editable={writable && userRole !== "Analyst"}
						onStatusClick={handleAttendanceStatus}
						onAllPresent={markAllPresent}
					/>
				</div>
			),
		},
		{
			key: "tactical_board",
			label: "Lavagna tattica",
			hidden: userRole === "Player",
			render: () => (
				<TacticalBoardPanel
					key={id}
					entityType="training"
					entityId={id}
					attachments={attachments}
					onRefresh={getTrainingAttachment}
					selectedId={selectedBoardId}
					onSelect={setSelectedBoardId}
					editable={!["Player", "Analyst"].includes(userRole)}
					onDelete={(attachmentId) => {
						setEntityToDelete({type: "attachment", id: attachmentId});
						setOpenHandleConfirm(true);
					}}
				/>
			),
		},
		{
			key: "training_exercises",
			label: t("exercises"),
			hidden: userRole === "Player",
			render: () => (
				<ExercisesTab
					exercises={trainingExercises}
					onAdd={(phaseId) => {
						setInitialData({phase: String(phaseId)});
						setOpenExerciseModal(true);
					}}
					onEdit={(te) => {
						setInitialData(te);
						setOpenExerciseModal(true);
					}}
					onDelete={(te) => {
						setEntityToDelete({type: "exercise", id: te.id});
						setOpenHandleConfirm(true);
					}}
					onReorder={handleExerciseOrder}
				/>
			),
		},
		{
			key: "rpe",
			label: "RPE",
			hidden: !hasRPE,
			render: () => {
				return (
					<div className="row no-margin rpe-tab-container g-2">
						{userRole === "Player" ? (
							[
								<div
									key="convocation"
									className="col-sm-12 col-lg-6 padding-small"
								>
									<div className="player-choice d-flex flex-column align-items-center justify-content-center h-100">
										<div className="choice-label">
											Hai partecipato all'allenamento?
										</div>
										<div className="choice-options">
											{attendanceStatusOptions.map((as, k) => {
												return (
													<div
														key={k}
														className={
															"single-option" +
															(attendance === as.value ? " selected" : "")
														}
														onClick={() => setAttendance(as.value)}
													>
														{as.label}
													</div>
												);
											})}
										</div>
									</div>
								</div>,
								<div key="rpe" className="col-sm-12 col-lg-6 padding-small">
									<div className="player-choice d-flex flex-column align-items-center justify-content-center h-100 lh-1">
										<div className="choice-label">RPE</div>
										<Rating
											selectedRating={formData.rpe}
											setSelectedRating={(value) => {
												setFormData({...formData, rpe: value});
											}}
											max={10}
											min={1}
										/>
										<div>
											{
												rpeOptions.filter(
													(o) => o.value === formData.rpe?.toString(),
												)?.[0]?.label
											}
										</div>
									</div>
								</div>,
							]
						) : (
							<RpeTab
								matchData={{
									matchConvocations: trainingData.trainingConvocations,
									matchRpe: trainingData.trainingRPE,
								}}
								teamPlayers={allInternalPlayers}
								onEdit={(mr) => {
									setInitialData(buildInitialData({...mr}));
									setOpenRPEModal(true);
								}}
								onDelete={(mr) => {
									setEntityToDelete({type: "rpe", id: mr.id});
									setOpenHandleConfirm(true);
								}}
							/>
						)}
					</div>
				);
			},
		},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					trainingData?.startDate
						? moment(trainingData.startDate).format("DD/MM/YYYY HH:mm") +
							" - " +
							moment(trainingData.endDate).format("HH:mm")
						: ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<Tabs
				tabs={tabs.map((tab) =>
					tab.key === currentTab && tabActions.length
						? {
								...tab,
								render: () => (
									<>
										<div className="main-actions-bar">
											<MainActions actions={tabActions} />
										</div>
										{tab.render()}
									</>
								),
							}
						: tab,
				)}
				currentTab={currentTab}
				setCurrentTab={(tab) => dispatch(setTrainingTab(tab))}
			/>
			{openCopyExercisesModal ? (
				<HandleCopyExercisesModal
					open={openCopyExercisesModal}
					confirm={handleCopyExercises}
					close={() => setOpenCopyExercisesModal(false)}
					trainingCategoryId={trainingData?.playersCategoryId}
				/>
			) : null}
			{openExerciseModal ? (
				<HandleExerciseModal
					open={openExerciseModal}
					initialData={initialData}
					confirm={handleExercise}
					close={handleClose}
					handleNotification={handleNotification}
				/>
			) : null}
			{openRPEModal ? (
				<HandleRPEModal
					open={openRPEModal}
					confirm={handleMeasurement}
					close={handleClose}
					initialData={initialData}
					internalTeams={internalTeams}
					players={allInternalPlayers?.filter((p) => {
						return (
							initialData?.id ||
							!trainingData?.trainingRPE?.find((r) => r.playerId === p.id)
						);
					})}
				/>
			) : null}
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => {
					switch (entityToDelete?.type) {
						case "training":
							return deleteTraining();
						case "attachment":
							return deleteAttachment();
						case "rpe":
							return deleteMeasurement();
						case "exercise":
							return deleteExercise();
						default:
							return;
					}
				}}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
				disabled={loading}
			/>
			<VLFModal
				open={Boolean(injuryWarning)}
				title="Giocatore infortunato"
				content={
					<div>
						{injuryWarning}: infortunio attivo alla data dell’evento. Vuoi
						confermare la presenza?
					</div>
				}
				confirm={() => resolveInjuryWarning(true)}
				close={() => resolveInjuryWarning(false)}
				confirmLabel="Conferma presenza"
			/>
		</Page>
	);
}

export default TrainingPage;
